/**
 * Shared chart types. Every chart in this folder builds on these so apps
 * can swap one chart for another without rewiring data plumbing.
 *
 * The data shape we standardise on is "rows of one record per category",
 * with the category in the `name` field and any number of numeric series
 * keyed by series name. e.g. `{ name: "Q1", revenue: 120, cost: 80 }`.
 */

import type { CSSProperties } from "react"

export type ChartDatum = Record<string, string | number | null | undefined>

export interface BaseChartProps {
  /** Row-oriented data. Each row must contain a `name` (or a custom `xKey`)
   *  and any numeric fields that should be plotted. */
  data: ChartDatum[]
  /** Series field names to plot (e.g. ["revenue", "cost"]).
   *  Inferred from the first row's numeric keys when omitted. */
  series?: string[]
  /** Field name for the x-axis category. Defaults to "name". */
  xKey?: string
  /** Color list — looped across series. */
  palette?: string[]
  /** Optional series-name-to-color override. Wins over `palette`. */
  colorMap?: Record<string, string>
  showLegend?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  xLabel?: string
  yLabel?: string
  /** Render with no axes / grid / legend — sparkline mode. */
  compact?: boolean
  className?: string
  style?: CSSProperties
  /** Pixel height (number) or percentage (e.g. "100%"). */
  height?: ChartDimension
  /** Pixel width (number) or percentage (e.g. "100%"). */
  width?: ChartDimension
}

/** What ResponsiveContainer accepts for width/height. */
export type ChartDimension = number | `${number}%`

/** A reasonable default palette. Apps can override by passing `palette`. */
export const DEFAULT_PALETTE = [
  "#3b82f6", // blue
  "#10b981", // emerald
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#06b6d4", // cyan
  "#ec4899", // pink
  "#84cc16", // lime
  "#f97316", // orange
  "#6366f1", // indigo
]

/** Pull numeric series names from the first row when the caller didn't
 *  pass them explicitly. */
export function inferSeries(data: ChartDatum[], xKey: string = "name"): string[] {
  if (!data.length) return []
  const first = data[0]
  return Object.keys(first).filter(k => k !== xKey && typeof first[k] === "number")
}

export function colorFor(name: string, idx: number, palette: string[], colorMap?: Record<string, string>): string {
  if (colorMap && colorMap[name]) return colorMap[name]
  return palette[idx % palette.length]
}
